/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

// =========================================================================
// Structured Logger - level-gated console wrapper with basic redaction
// =========================================================================

import { redactPII } from "@/lib/ai/safety/pii";

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogContext {
  requestId?: string;
  organizationId?: string;
  userId?: string;
  feature?: string;
  [key: string]: unknown;
}

// Lazy evaluation to prevent compilation blocking in Next.js 16
// Only access process.env when actually needed, not on module import
function getIsProd(): boolean {
  return process.env.NODE_ENV === "production";
}

// Log level gating: suppress debug logs in production
const LOG_LEVEL_PRIORITY: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function getMinLevel(): LogLevel {
  return getIsProd() ? "info" : "debug";
}

function shouldLog(level: LogLevel): boolean {
  return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[getMinLevel()];
}

// PII and secrets: keys whose values must be redacted in logs (compliance)
const PII_KEY_PATTERN =
  /^(email|phone|firstName|lastName|first_name|last_name|email_address|phone_number|address|street_address|postal_code|city|state|ssn|date_of_birth|dob)$/i;
const SECRET_KEY_PATTERN = /password|secret|token|apiKey|authorization/i;

function isPiiKey(key: string): boolean {
  return PII_KEY_PATTERN.test(key);
}

function isSecretKey(key: string): boolean {
  return SECRET_KEY_PATTERN.test(key);
}

// PERFORMANCE: In dev, only redact secrets in strings (fast path).
// Full deep-clone redaction with PII scanning is production-only.
// This eliminates the WeakSet allocation + recursive object copying
// that was causing constant GC pressure (millions of temp objects).
const IS_DEV = process.env.NODE_ENV === "development";

function redactStringFast(value: string): string {
  return value
    .replace(/sk-[a-zA-Z0-9_-]+/g, "***")
    .replace(/(Bearer )([A-Za-z0-9\-._~+/]+=*)/g, "$1***")
    .replace(/api_key=([A-Za-z0-9\-._~+/]+=*)/gi, "api_key=***");
}

function redact(value: unknown, visited?: WeakSet<object>): unknown {
  // Dev fast path: only redact secrets in strings, pass everything else through
  if (IS_DEV) {
    if (typeof value === "string") return redactStringFast(value);
    return value;
  }

  // Production: full deep redaction with PII scanning
  if (typeof value === "string") {
    let out = redactStringFast(value);
    out = redactPII(out).text;
    return out;
  }

  // Lazily create visited set only when we actually need it (objects/arrays)
  if (!visited) visited = new WeakSet();

  if (Array.isArray(value)) {
    if (visited.has(value)) return "[Circular]";
    visited.add(value);
    return value.map((item) => redact(item, visited));
  }
  if (value && typeof value === "object") {
    if (visited.has(value as object)) return "[Circular]";
    visited.add(value as object);

    // Handle Next.js async params/searchParams - skip enumeration
    if (
      ("then" in value || "Symbol" in value || "toString" in value) &&
      (value.constructor?.name === "ReadonlyURLSearchParams" ||
        value.constructor?.name === "Params" ||
        Object.prototype.toString.call(value) === "[object Promise]")
    ) {
      return "[Async Object]";
    }

    const out: Record<string, unknown> = {};
    try {
      for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
        const isSecret = isSecretKey(k);
        const isPii = isPiiKey(k);
        const redactedKey = isSecret
          ? k.replace(/secret|password|token/gi, "***")
          : k;

        if (isSecret || isPii) {
          out[redactedKey] = "[REDACTED]";
        } else {
          out[redactedKey] = redact(v, visited);
        }
      }
    } catch {
      return "[Object - enumeration failed]";
    }
    return out;
  }
  return value;
}

function log(level: LogLevel, ...args: unknown[]): void {
  if (!shouldLog(level)) return;

  // ROOT CAUSE FIX for Next.js 16 static generation:
  // Next.js requires accessing request data (headers/cookies) before using new Date().
  // Since log() must be synchronous for console.* compatibility, we can't access headers()
  // inside this function. The solution: callers in Server Components must call
  // `await headers()` first before using logger functions.
  //
  // This is documented in the logger exports below with a clear error message if
  // someone tries to use it in a Server Component without accessing headers first.
  const timestamp = new Date().toISOString();

  // Derive message and context from variadic args to be resilient to console.* patterns
  // - If first arg is string, use as message; otherwise stringify/redact it
  let message: string = "";
  const rest: unknown[] = args.slice(1);

  if (args.length > 0) {
    const first = args[0];
    if (typeof first === "string") {
      // Redact secrets and PII in string messages
      message = redact(first) as string;
    } else if (first instanceof Error) {
      message = `${first.name}: ${first.message}`;
      rest.unshift({ stack: first.stack });
    } else {
      try {
        message = JSON.stringify(redact(first));
      } catch (error) {
        message = String(first);
      }
    }
  }

  // Build context from remaining args
  let context: Record<string, unknown> | undefined;
  if (rest.length === 1 && rest[0] && typeof rest[0] === "object") {
    const redacted = redact(rest[0]);
    // If it's an array, wrap it in an object to preserve structure
    if (Array.isArray(redacted)) {
      context = { data: redacted } as Record<string, unknown>;
    } else {
      context = redacted as Record<string, unknown>;
    }
  } else if (rest.length > 0) {
    context = { args: redact(rest) } as Record<string, unknown>;
  }

  const payload: Record<string, unknown> = {
    level,
    msg: message,
    ts: timestamp,
    ...(context ? context : {}),
  };

  // In prod, single-line JSON for ingestion; in dev, skip JSON.stringify
  // and let console handle formatting (avoids allocating a large string per log)
  if (IS_DEV) {
    const logMethod = level === "debug" ? "log" : level;
    /* eslint-disable no-console */
    if (typeof console[logMethod] === "function") {
      console[logMethod](payload);
    } else {
      console.log(payload);
    }
    /* eslint-enable no-console */
    return;
  }
  const line = JSON.stringify(payload);

  // Use a structured logging approach instead of direct console calls
  const logToOutput = (level: LogLevel, line: string): void => {
    // Edge Runtime compatible logging - avoid process.stdout
    try {
      // Use console methods which are available in Edge Runtime
      const logMethod = level === "debug" ? "log" : level;
      /* eslint-disable no-console */
      if (typeof console[logMethod] === "function") {
        console[logMethod](line);
      } else {
        console.log(line);
      }
      /* eslint-enable no-console */
    } catch (error) {
      // Fallback for any console issues
      /* eslint-disable no-console */
      console.log(`[${level.toUpperCase()}] ${message}`);
      /* eslint-enable no-console */
    }
  };

  switch (level) {
    case "debug":
      logToOutput("debug", line);
      break;
    case "info":
      logToOutput("info", line);
      break;
    case "warn":
      logToOutput("warn", line);
      break;
    case "error":
      logToOutput("error", line);
      break;
    default:
      logToOutput("info", line); // Use 'info' as default instead of 'log'
  }
}

/**
 * Logger for application-wide logging
 *
 * ⚠️ IMPORTANT: In Next.js 16 Server Components, you MUST call `await headers()`
 * or `await cookies()` BEFORE using any logger methods. This is required for
 * static generation compatibility. Next.js requires accessing request data
 * before using dynamic values like new Date().
 *
 * @example
 * ```ts
 * import { headers } from 'next/headers';
 * import { logger } from '@/lib/utils/logger';
 *
 * export default async function MyLayout() {
 *   await headers(); // REQUIRED: Must be called first
 *   logger.info('This is safe to use now');
 * }
 * ```
 */
export const logger = {
  debug: (...args: unknown[]): void => log("debug", ...args),
  info: (...args: unknown[]): void => log("info", ...args),
  warn: (...args: unknown[]): void => log("warn", ...args),
  error: (...args: unknown[]): void => log("error", ...args),
};

/** Redact PII and secrets from structured data. Use for audit trails and logs. */
export { redact };

export type { LogContext };
